import React, { useState } from "react";
import AddNewPatient from "../addPatients";
import { TableColumnsItem } from "../../../../interfaces/components/mainTable";
import { ButtonType, ColumnType, ModalType } from "../../../../shared/utils/enums";
import MainTable from "../../../../components/mainTable";
import { useDispatch } from "react-redux";
import { showModal } from "../../../../connected-components/modal/reducer";
import { screens } from "../../../../config/routes";
import { useNavigate } from "react-router-dom";
import { store } from "../../../../redux/store";
import { fetchPatients } from "./actions";
import { Patient } from "../../../../interfaces/api/patients";
import { Trash } from "lucide-react";
import patientsApi from "../../../../api/patients";
import { toastService } from "../../../../shared/utils/toast";
import { showLoading, hideLoading } from "../../../../connected-components/loader/reducer";

const PatientsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const loadPatients = async () => {
      setIsLoading(true);
      const res = await fetchPatients(currentPage + 1, pageSize);
      if (res && res.result === 'success' && res.content && res.content.data) {
        setPatients(res.content.data);
      }
      setIsLoading(false);
    };
    loadPatients();
    // eslint-disable-next-line
  }, [currentPage, pageSize]);

  const handleRowPress = (patient: Patient) => {
    navigate(screens.patientDetails?.replace(':id', String(patient.id)));
  };

  const handlePageChange = ({
    pageIndex,
    pageSize: newPageSize,
  }: {
    pageIndex: number;
    pageSize: number;
  }) => {
    setCurrentPage(pageIndex);
    setPageSize(newPageSize);
  };

  const handleAddNewPatient = () => {
    dispatch(showModal({
      type: ModalType.COMPONENT,
      title: 'Add New Patient',
      component: <AddNewPatient />,
      onClose: async () => {
        setIsLoading(true);
        const res = await fetchPatients(currentPage + 1, pageSize);
        if (res && res.result === 'success' && res.content && res.content.data) {
          setPatients(res.content.data);
        }
        setIsLoading(false);
      }
    }));
  };

  const handleDeletePatient = (patient: Patient) => {
    dispatch(
      showModal({
        type: ModalType.QUESTION,
        title: 'Delete Patient',
        message: 'Are you sure you want to delete this patient? This action cannot be undone.',
        onConfirm: async () => {
          try {
            dispatch(showLoading());
            await patientsApi.deletePatient(String(patient.id));
            toastService.showSuccessToast('Patient deleted successfully');
            setPatients((prev) => prev.filter((p) => p.id !== patient.id));
          } catch (err) {
            toastService.showErrorToast('Failed to delete patient');
          } finally {
            dispatch(hideLoading());
          }
        },
        onCancel: () => {},
      })
    );
  };

  const getActionButtonClasses = (type: string) => {
    const baseClasses = "p-2 rounded-full transition-colors duration-200";
    switch (type) {
      case "delete":
        return `${baseClasses} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10`;
      default:
        return `${baseClasses} text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-500/10`;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const patientTableColumns: TableColumnsItem[] = [
    {
      key: "fullName",
      name: "Patient Name",
      type: ColumnType.CUSTOM,
      width: "220px",
      cardTitle: true,
      customValue: (row: Patient) =>
        `${row.first_name} ${row.last_name}`.trim(),
      customCellClass: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "dob",
      name: "DOB",
      type: ColumnType.CUSTOM,
      width: "120px",
      cardOrder: 1,
      customValue: (row: Patient) => formatDate(row.dob),
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "gender",
      name: "Gender",
      type: ColumnType.TEXT,
      width: "100px",
      cardOrder: 2,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "mobile_number",
      name: "Phone",
      type: ColumnType.TEXT,
      width: "140px",
      cardOrder: 3,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "email",
      name: "Email",
      type: ColumnType.TEXT,
      width: "220px",
      cardOrder: 4,
      customCellClass: "text-gray-600 dark:text-gray-400",
    },
    {
      key: "actions",
      name: "Actions",
      type: ColumnType.BUTTON,
      width: "100px",
      cardOrder: 999,
      buttonsList: [
        {
          type: ButtonType.ICON,
          onPress: (e: React.MouseEvent, row: Patient) => {
            e.stopPropagation();
            handleDeletePatient(row);
          },
          cssClass: getActionButtonClasses("delete"),
          icon: Trash,
        },
      ],
    },
  ];

  return (
    <div className="py-4">
      <MainTable
        columns={patientTableColumns}
        data={patients}
        isLoading={isLoading}
        tableClass="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        onRowPress={handleRowPress}
        showPagination={true}
        totalData={patients.length}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChanged={handlePageChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearch={true}
        addButtonLabel="Add New Patient"
        onAddNewItemPress={handleAddNewPatient}
      />
    </div>
  );
};

export default PatientsList;